import React, { useState, useEffect } from 'react';
import { 
  UserPlus, 
  Search, 
  Edit2, 
  Trash2, 
  Mail, 
  Phone, 
  Users,
  X,
  Save,
  AlertCircle,
  MessageSquare,
  Upload,
  FileSpreadsheet,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { db } from '../../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { COLLECTIONS } from '../../firebase/collections';
import toast from 'react-hot-toast';
import { canAddStudent } from '../../utils/subscriptionPricing';
import StudentLimitModal from '../common/StudentLimitModal';
import { useRealtime } from '../../contexts/RealtimeContext';
import { generateRegistrationNumber } from '../../utils/registrationNumber';

const StudentManagement = ({ coachingId }) => {
  const { userProfile } = useRealtime();
  const [students, setStudents] = useState([]);
  const [studentBatches, setStudentBatches] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedForWhatsApp, setSelectedForWhatsApp] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    fatherName: '',
    motherName: '',
    phone: ''
  });

  const [bulkData, setBulkData] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  // Helper function to sync student with their user profile if it exists
  const syncStudentWithUserProfile = async (studentEmail, studentName, studentPhone) => {
    try {
      const usersRef = collection(db, COLLECTIONS.USERS || 'users');
      const q = query(usersRef, where('email', '==', studentEmail.toLowerCase().trim()));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // User exists, update their coachingId
        const userDoc = snapshot.docs[0];
        await updateDoc(doc(db, COLLECTIONS.USERS || 'users', userDoc.id), {
          coachingId: coachingId,
          // Only update name and phone if they are not set
          ...(!userDoc.data().name && { name: studentName }),
          ...(!userDoc.data().displayName && { displayName: studentName }),
          ...(!userDoc.data().phone && studentPhone && { phone: studentPhone }),
          updatedAt: serverTimestamp()
        });
        console.log(`Synced coachingId ${coachingId} for user ${studentEmail}`);
      }
    } catch (error) {
      console.error('Error syncing student with user profile:', error);
    }
  };

  // OPTIMIZED: Load students with caching and polling instead of real-time listeners
  useEffect(() => {
    loadStudents();
    
    // Refresh every 5 minutes instead of real-time listeners
    const refreshInterval = setInterval(() => {
      loadStudents();
    }, 300000); // 5 minutes
    
    return () => clearInterval(refreshInterval);
  }, [coachingId]);
  
  // Reload batches when students change
  useEffect(() => {
    if (students.length > 0) {
      loadStudentBatches(students);
    }
  }, [students.length]);

  // OPTIMIZED: Load students with caching
  const loadStudents = async () => {
    try {
      setLoading(true);
      
      // Check cache first (5 minute TTL)
      const cacheKey = `students_${coachingId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        const cachedData = JSON.parse(cached);
        setStudents(cachedData.students);
        setStudentBatches(cachedData.batches);
        setLoading(false);
        return;
      }
      
      const studentsRef = collection(db, 'students');
      const q = query(studentsRef, where('coachingId', '==', coachingId));
      const snapshot = await getDocs(q);
      
      const studentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Don't auto-assign registration numbers on load - only when user clicks button
      // await assignMissingRegistrationNumbers(studentsData);
      
      setStudents(studentsData);
      
      // Load batch enrollments for all students
      const batchesData = await loadStudentBatches(studentsData);
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify({
        students: studentsData,
        batches: batchesData
      }));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

// Assign registration numbers to existing students who don't have them or have duplicate numeric sequences
  const assignMissingRegistrationNumbers = async (studentsData) => {
    try {
      // Determine duplicate sequence numbers (last 5 digits) and missing numbers
      const suffixCount = new Map();
      const duplicateSuffixes = new Set();

      studentsData.forEach(student => {
        if (!student.registrationNumber) return;
        const suffix = student.registrationNumber.slice(-5);
        const suffixNum = parseInt(suffix, 10);
        if (isNaN(suffixNum)) return;

        const count = (suffixCount.get(suffixNum) || 0) + 1;
        suffixCount.set(suffixNum, count);
        if (count > 1) duplicateSuffixes.add(suffixNum);
      });

      const studentsNeedingRegNum = studentsData.filter(student => {
        if (!student.registrationNumber) return true;
        const suffix = student.registrationNumber.slice(-5);
        const suffixNum = parseInt(suffix, 10);
        return isNaN(suffixNum) || duplicateSuffixes.has(suffixNum);
      });

      console.log('Suffix counts:', Object.fromEntries(suffixCount));
      console.log('Duplicate suffixes:', Array.from(duplicateSuffixes));
      console.log('Students needing reg num:', studentsNeedingRegNum.length);

      if (studentsNeedingRegNum.length === 0) {
        toast.success('All students already have unique registration numbers.');
        return;
      }

      // Get all existing registration number suffixes for this coaching and find max used number
      const studentsRef = collection(db, 'students');
      const q = query(studentsRef, where('coachingId', '==', coachingId));
      const snapshot = await getDocs(q);

      const existingNumbers = new Set();
      snapshot.forEach(doc => {
        const data = doc.data();
        if (!data.registrationNumber) return;
        const suffix = data.registrationNumber.slice(-5);
        const suffixNum = parseInt(suffix, 10);
        if (!isNaN(suffixNum)) existingNumbers.add(suffixNum);
      });

      let maxNumber = 0;
      existingNumbers.forEach(num => {
        if (num > maxNumber) maxNumber = num;
      });

      console.log(`maxNumber from DB: ${maxNumber}, starting assignment from ${maxNumber + 1}`);

      const { getCoachingNameAbbrev } = await import('../../utils/registrationNumber');
      const schoolAbbrev = await getCoachingNameAbbrev(coachingId);
      const currentYear = new Date().getFullYear();

      studentsNeedingRegNum.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return aTime - bTime;
      });

      let nextNumber = maxNumber + 1;
      let successCount = 0;
      let errorCount = 0;

      for (const student of studentsNeedingRegNum) {
        try {
          const formattedNumber = String(nextNumber).padStart(5, '0');
          const studentNameAbbrev = (student.name || 'STU').substring(0, 3).toUpperCase();
          const registrationNumber = `MAHSTRY${currentYear}${schoolAbbrev}${studentNameAbbrev}${formattedNumber}`;

          console.log(`Assigning ${registrationNumber} to ${student.name || student.email} (old: ${student.registrationNumber || 'none'})`);

          await updateDoc(doc(db, 'students', student.id), {
            registrationNumber,
            updatedAt: serverTimestamp()
          });

          student.registrationNumber = registrationNumber;
          nextNumber += 1;
          successCount += 1;
        } catch (error) {
          errorCount += 1;
          console.error('Error updating student registration number', student.id, student.name, error);
        }
      }

      console.log(`Registration numbers updated: ${successCount}, failed: ${errorCount}`);
      if (errorCount) {
        toast.error(`Updated ${successCount} students, ${errorCount} failed`);
      } else {
        toast.success(`Successfully updated ${successCount} registration numbers`);
      }
    } catch (error) {
      console.error('Error in assignMissingRegistrationNumbers:', error);
      toast.error('Error generating registration numbers. Check console for details.');
    }
  };

  const loadStudentBatches = async (studentsData) => {
    try {
      const batchesMap = {};
      
      // Get all enrollments for this coaching
      const enrollmentsRef = collection(db, 'batchEnrollments');
      const enrollmentsQuery = query(enrollmentsRef, where('coachingId', '==', coachingId));
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      
      // Get all batch IDs
      const batchIds = [...new Set(enrollmentsSnapshot.docs.map(doc => doc.data().batchId))];
      
      // Load batch details
      const batchesRef = collection(db, 'batches');
      const batchesQuery = query(batchesRef, where('coachingId', '==', coachingId));
      const batchesSnapshot = await getDocs(batchesQuery);
      
      const batchesById = {};
      batchesSnapshot.docs.forEach(doc => {
        batchesById[doc.id] = { id: doc.id, ...doc.data() };
      });
      
      // Map enrollments to students
      enrollmentsSnapshot.docs.forEach(doc => {
        const enrollment = doc.data();
        const studentEmail = enrollment.email?.toLowerCase();
        
        if (studentEmail) {
          if (!batchesMap[studentEmail]) {
            batchesMap[studentEmail] = [];
          }
          
          const batch = batchesById[enrollment.batchId];
          if (batch) {
            batchesMap[studentEmail].push({
              batchId: enrollment.batchId,
              batchName: batch.name,
              subject: batch.subject,
              enrolledAt: enrollment.enrolledAt
            });
          }
        }
      });
      
      setStudentBatches(batchesMap);
      return batchesMap; // Return for caching
    } catch (error) {
      console.error('Error loading student batches:', error);
      return {};
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    // Check student limit before adding
    if (userProfile?.studentRange) {
      const limitCheck = canAddStudent(students.length, userProfile.studentRange);
      
      if (!limitCheck.canAdd) {
        toast.error(limitCheck.message);
        setShowAddModal(false);
        setShowLimitModal(true);
        return;
      }

      // Show warning if approaching limit
      if (limitCheck.isApproaching) {
        toast.warning(limitCheck.message);
      }
    }

    try {
      // Generate registration number
      const registrationNumber = await generateRegistrationNumber({
        coachingId,
        studentName: formData.name
      });

      const studentsRef = collection(db, 'students');
      const newStudentRef = await addDoc(studentsRef, {
        ...formData,
        coachingId,
        registrationNumber,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      console.log(`Added student ${formData.name} with registration number: ${registrationNumber}`);

      // Sync with user profile if it exists
      if (formData.email) {
        await syncStudentWithUserProfile(formData.email, formData.name, formData.phone);
      }

      toast.success(`Student added successfully. Registration Number: ${registrationNumber}`);
      setShowAddModal(false);
      resetForm();
      
      // Invalidate cache and reload
      localStorage.removeItem(`students_${coachingId}`);
      localStorage.removeItem(`students_${coachingId}_ts`);
      loadStudents();
    } catch (error) {
      console.error('Error adding student:', error);
      toast.error('Failed to add student');
    }
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent) return;

    try {
      const studentRef = doc(db, 'students', selectedStudent.id);
      await updateDoc(studentRef, {
        ...formData,
        updatedAt: serverTimestamp()
      });

      toast.success('Student updated successfully');
      setShowEditModal(false);
      setSelectedStudent(null);
      resetForm();
      
      // Invalidate cache and reload
      localStorage.removeItem(`students_${coachingId}`);
      localStorage.removeItem(`students_${coachingId}_ts`);
      loadStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error('Failed to update student');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student? This will also remove them from all enrolled batches.')) {
      return;
    }

    try {
      // Get student data first
      const studentDoc = await getDocs(query(collection(db, 'students'), where('__name__', '==', studentId)));
      if (studentDoc.empty) {
        toast.error('Student not found');
        return;
      }
      
      const studentData = studentDoc.docs[0].data();
      const studentEmail = studentData.email?.toLowerCase();
      
      // Delete all batch enrollments for this student
      if (studentEmail) {
        const enrollmentsRef = collection(db, 'batchEnrollments');
        const enrollmentsQuery = query(
          enrollmentsRef, 
          where('email', '==', studentEmail),
          where('coachingId', '==', coachingId)
        );
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        
        const deletePromises = enrollmentsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        
        console.log(`Deleted ${enrollmentsSnapshot.size} enrollments for student ${studentEmail}`);
      }
      
      // Delete the student document
      const studentRef = doc(db, 'students', studentId);
      await deleteDoc(studentRef);
      
      toast.success('Student and all enrollments deleted successfully');
      
      // Invalidate cache and reload
      localStorage.removeItem(`students_${coachingId}`);
      localStorage.removeItem(`students_${coachingId}_ts`);
      loadStudents();
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete student');
    }
  };

  const openEditModal = (student) => {
    setSelectedStudent(student);
    setFormData({
      name: student.name || '',
      email: student.email || '',
      fatherName: student.fatherName || '',
      motherName: student.motherName || '',
      phone: student.phone || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      fatherName: '',
      motherName: '',
      phone: ''
    });
    setBulkData([]);
    setUploadError(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        // Validate data format
        const validatedData = data.map((row, index) => {
          const student = {
            name: row.Name || row.name || row['Student Name'] || '',
            email: row.Email || row.email || '',
            phone: String(row.Phone || row.phone || row['Phone Number'] || ''),
            fatherName: row.FatherName || row.fatherName || row["Father's Name"] || '',
            motherName: row.MotherName || row.motherName || row["Mother's Name"] || '',
          };

          if (!student.name || !student.email) {
            throw new Error(`Row ${index + 2}: Name and Email are required`);
          }

          // Simple email validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(student.email)) {
            throw new Error(`Row ${index + 2}: Invalid email format - ${student.email}`);
          }

          return student;
        });

        setBulkData(validatedData);
        setUploadError(null);
      } catch (err) {
        setUploadError(err.message);
        setBulkData([]);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleBulkAddStudents = async () => {
    if (bulkData.length === 0) return;

    // Check student limit
    if (userProfile?.studentRange) {
      const limitCheck = canAddStudent(students.length + bulkData.length, userProfile.studentRange);
      if (!limitCheck.canAdd) {
        toast.error(`Bulk upload exceeds your student limit. You can only add ${limitCheck.remainingCount} more students.`);
        setShowLimitModal(true);
        return;
      }
    }

    setIsUploading(true);
    try {
      const studentsRef = collection(db, 'students');
      
    setIsUploading(true);
    try {
      const studentsRef = collection(db, 'students');
      
      // Get all existing registration numbers to find the highest unique number
      const q = query(studentsRef, where('coachingId', '==', coachingId));
      const snapshot = await getDocs(q);
      
      // Collect all existing registration numbers
      const existingNumbers = new Set();
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.registrationNumber) {
          const lastFiveDigits = data.registrationNumber.slice(-5);
          const numValue = parseInt(lastFiveDigits, 10);
          if (!isNaN(numValue)) {
            existingNumbers.add(numValue);
          }
        }
      });

      // Find the highest existing number
      let maxNumber = 0;
      existingNumbers.forEach(num => {
        if (num > maxNumber) maxNumber = num;
      });

      console.log(`Bulk upload: Existing numbers [${Array.from(existingNumbers).sort().join(', ')}], max: ${maxNumber}, starting from: ${maxNumber + 1}`);

      // Get coaching name once to avoid multiple lookups
      const { getCoachingNameAbbrev } = await import('../../utils/registrationNumber');
      const schoolAbbrev = await getCoachingNameAbbrev(coachingId);
      const currentYear = new Date().getFullYear();

      // Upload students sequentially to maintain proper numbering
      for (let i = 0; i < bulkData.length; i++) {
        const student = bulkData[i];
        
        try {
          const sequenceNumber = maxNumber + i + 1;
          const formattedNumber = String(sequenceNumber).padStart(5, '0');
          const studentNameAbbrev = (student.name || 'STU').substring(0, 3).toUpperCase();
          const registrationNumber = `MAHSTRY${currentYear}${schoolAbbrev}${studentNameAbbrev}${formattedNumber}`;

          console.log(`[${i + 1}/${bulkData.length}] Adding ${student.name} with reg# ${registrationNumber} (sequence: ${sequenceNumber})`);

          await addDoc(studentsRef, {
            ...student,
            coachingId,
            registrationNumber,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          
          // Sync each bulk student with their user profile if it exists
          if (student.email) {
            await syncStudentWithUserProfile(student.email, student.name, student.phone);
          }

          console.log(`✓ Added: ${student.name} -> ${registrationNumber}`);
        } catch (error) {
          console.error(`✗ Error adding ${student.name}:`, error);
          toast.error(`Failed to add student: ${student.name}`);
        }
      }

      toast.success(`${bulkData.length} students added successfully with sequential registration numbers`);
      setShowBulkUploadModal(false);
      resetForm();
      
      // Invalidate cache and reload
      localStorage.removeItem(`students_${coachingId}`);
      localStorage.removeItem(`students_${coachingId}_ts`);
      loadStudents();
    } catch (error) {
      console.error('Error in bulk upload:', error);
      toast.error('Failed to upload students. Please check the data and try again.');
    } finally {
      setIsUploading(false);
    }
    } catch (error) {
      console.error('Error in bulk upload:', error);
      toast.error('Failed to upload students. Please check the data and try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSampleExcel = () => {
    const sampleData = [
      {
        'Name': 'John Doe',
        'Email': 'john@example.com',
        'Phone': '9876543210',
        "Father's Name": 'Robert Doe',
        "Mother's Name": 'Mary Doe'
      },
      {
        'Name': 'Jane Smith',
        'Email': 'jane@example.com',
        'Phone': '8877665544',
        "Father's Name": 'Richard Smith',
        "Mother's Name": 'Susan Smith'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, "Student_Upload_Sample.xlsx");
  };

  const filteredStudents = students.filter(student => {
    if (!searchTerm || !searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    const name = (student.name || student.fullName || student.displayName || student.studentName || '').toLowerCase();
    const email = (student.email || student.studentEmail || '').toLowerCase();
    const phone = student.phone || '';
    const fatherName = (student.fatherName || '').toLowerCase();
    const motherName = (student.motherName || '').toLowerCase();
    const regNum = (student.registrationNumber || '').toLowerCase();
    return name.includes(term) ||
      email.includes(term) ||
      phone.includes(term) ||
      fatherName.includes(term) ||
      motherName.includes(term) ||
      regNum.includes(term);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Users className="w-6 h-6 mr-2" />
              Student Management
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage student information and records • Total Students: {students.length}
              {userProfile?.studentRange && (() => {
                const limitCheck = canAddStudent(students.length, userProfile.studentRange);
                const maxDisplay = limitCheck.maxAllowed === 'Unlimited' ? '∞' : limitCheck.maxAllowed;
                const remaining = limitCheck.remaining === 'Unlimited' ? '∞' : limitCheck.remaining;
                const isNearLimit = typeof remaining === 'number' && remaining <= 10;
                
                return (
                  <span className={isNearLimit ? 'text-orange-600 dark:text-orange-400 font-semibold' : ''}>
                    {' '}/ {maxDisplay} ({remaining} slots remaining)
                  </span>
                );
              })()}
              {' '}• Enrolled in Batches: {Object.keys(studentBatches).length}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={async () => {
                toast.loading('Fixing registration numbers for students...');
                await assignMissingRegistrationNumbers(students);
                // Clear cache to reload
                localStorage.removeItem(`students_${coachingId}`);
                localStorage.removeItem(`students_${coachingId}_ts`);
                await loadStudents();
                toast.dismiss();
                toast.success('Registration numbers fixed successfully!');
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors"
              title="Fix registration numbers for students with duplicates or missing numbers"
            >
              <Users className="w-5 h-5" />
              <span>Fix Reg. Numbers</span>
            </button>
            <button
              onClick={downloadSampleExcel}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm transition-colors"
              title="Download Sample Format"
            >
              <Download className="w-5 h-5" />
              <span>Sample Format</span>
            </button>
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors"
            >
              <FileSpreadsheet className="w-5 h-5" />
              <span>Bulk Upload</span>
            </button>
            <button
              onClick={() => {
                // Check limit before opening modal
                if (userProfile?.studentRange) {
                  const limitCheck = canAddStudent(students.length, userProfile.studentRange);
                  if (!limitCheck.canAdd) {
                    toast.error(limitCheck.message);
                    setShowLimitModal(true);
                    return;
                  }
                }
                resetForm();
                setShowAddModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors"
            >
              <UserPlus className="w-5 h-5" />
              <span>Add Student</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, phone, parent name, or reg. number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Students List */}
      <div className="p-6">
        {filteredStudents.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm ? 'No students found matching your search' : 'No students added yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Registration #
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Name
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Email
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Father's Name
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Mother's Name
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Phone
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Enrolled Batches
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student) => (
                  <tr
                    key={student.id}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="py-3 px-4">
                      <div className="font-mono text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded w-fit">
                        {student.registrationNumber || '-'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {student.name}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <Mail className="w-4 h-4 mr-2" />
                        {student.email}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {student.fatherName || '-'}
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {student.motherName || '-'}
                    </td>
                    <td className="py-3 px-4">
                      {student.phone ? (
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <Phone className="w-4 h-4 mr-2" />
                          {student.phone}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {studentBatches[student.email?.toLowerCase()]?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {studentBatches[student.email?.toLowerCase()].map((batch, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 cursor-help"
                              title={`${batch.batchName} - ${batch.subject}\nEnrolled: ${batch.enrolledAt ? new Date(batch.enrolledAt.seconds * 1000).toLocaleDateString() : 'N/A'}`}
                            >
                              {batch.batchName}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Not enrolled</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        {student.phone && (
                          <button
                            onClick={() => setSelectedForWhatsApp(student)}
                            className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                            title="Send WhatsApp message"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(student)}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                          title="Edit student"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteStudent(student.id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                          title="Delete student"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Student Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Add New Student
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddStudent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Student Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Father's Name
                </label>
                <input
                  type="text"
                  value={formData.fatherName}
                  onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mother's Name
                </label>
                <input
                  type="text"
                  value={formData.motherName}
                  onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="+91 1234567890"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>Add Student</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit Student
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedStudent(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateStudent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Registration Number
                </label>
                <input
                  type="text"
                  value={selectedStudent?.registrationNumber || '-'}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 cursor-not-allowed font-mono text-sm font-semibold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Student Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Father's Name
                </label>
                <input
                  type="text"
                  value={formData.fatherName}
                  onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mother's Name
                </label>
                <input
                  type="text"
                  value={formData.motherName}
                  onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="+91 1234567890"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedStudent(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>Update Student</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Student Limit Modal */}
      <StudentLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        currentCount={students.length}
        maxAllowed={userProfile?.studentRange ? canAddStudent(students.length, userProfile.studentRange).maxAllowed : 'N/A'}
        onUpgrade={() => {
          setShowLimitModal(false);
          // Navigate to subscription management page
          window.location.href = '/admin/subscription';
        }}
      />

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <FileSpreadsheet className="mr-2 text-green-600" size={24} />
                Bulk Student Upload
              </h2>
              <button 
                onClick={() => {
                  setShowBulkUploadModal(false);
                  resetForm();
                }} 
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  1. Download the sample Excel format
                </label>
                <button
                  onClick={downloadSampleExcel}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  <Download size={18} />
                  <span>Download Sample Format</span>
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  2. Upload your Excel file
                </label>
                <div className="relative border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-8 text-center hover:border-primary-500 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <Upload className="mx-auto text-gray-400 mb-3" size={32} />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Click to browse or drag and drop your file here
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Supports .xlsx, .xls, .csv
                  </p>
                </div>
              </div>

              {uploadError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3 text-red-700">
                  <AlertCircle size={20} className="shrink-0" />
                  <div className="text-sm">{uploadError}</div>
                </div>
              )}

              {bulkData.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                      <Users size={18} className="mr-2 text-primary-600" />
                      Preview: {bulkData.length} students found
                    </label>
                    <button 
                      onClick={() => setBulkData([])}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                        <tr>
                          <th className="px-4 py-2">Name</th>
                          <th className="px-4 py-2">Email</th>
                          <th className="px-4 py-2">Phone</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {bulkData.slice(0, 50).map((s, idx) => (
                          <tr key={idx} className="bg-white dark:bg-gray-800">
                            <td className="px-4 py-2">{s.name}</td>
                            <td className="px-4 py-2">{s.email}</td>
                            <td className="px-4 py-2">{s.phone}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkData.length > 50 && (
                      <div className="p-2 text-center text-xs text-gray-500 italic">
                        Showing first 50 of {bulkData.length} students
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex space-x-4 sticky bottom-0 bg-white dark:bg-gray-800 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={bulkData.length === 0 || isUploading}
                  onClick={handleBulkAddStudents}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      <span>Upload {bulkData.length} Students</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement;
